import { useSearch } from "@tanstack/react-router";
import { useBlockCreator } from "@/api/blocklist";
import { useDismissHero } from "@/api/user-preferences";
import { PageHeroKey } from "@/components/application/page-hero/page-hero";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTrackedBlockCreators } from "@/hooks/use-tracked-block-creators";
import { error, success } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";

interface BlockCreatorModalProps {
  // A single creator (row / drawer): [creatorOpenId]. Multiple distinct creators (multi-select):
  // one entry per creator, already deduped by the caller.
  creatorOpenIds: string[];
  creatorName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BlockCreatorModal({ creatorOpenIds, creatorName, open, onOpenChange }: BlockCreatorModalProps) {
  const blockOne = useBlockCreator();
  const blockMany = useTrackedBlockCreators();
  const busy = blockOne.isPending || blockMany.isPending;
  const isBulk = creatorOpenIds.length > 1;
  const { fromEmptyState } = useSearch({ strict: false }) as { fromEmptyState?: boolean };
  const dismissHero = useDismissHero();

  const handleBlock = async () => {
    try {
      if (isBulk) {
        const blocked = await blockMany.block(creatorOpenIds);
        if (blocked !== null) {
          success(
            `${blocked} ${pluralize(blocked, "Creator", "Creators")} blocked. Unblock anytime from Operations/Blocked Creators.`,
          );
        }
      } else {
        await blockOne.mutateAsync(creatorOpenIds[0]);
        success("1 Creator blocked. Unblock anytime from Operations/Blocked Creators.");
      }
      if (fromEmptyState) dismissHero.mutate(PageHeroKey.BlockedCreators);
      onOpenChange(false);
    } catch {
      error("Failed to block creator");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="pt-2">
          <DialogTitle className="mb-3">
            {isBulk ? `Block ${creatorOpenIds.length} creators?` : `Block ${creatorName ?? "this creator"}?`}
          </DialogTitle>
          <DialogDescription className="mb-5">
            {isBulk
              ? "They'll be removed from your recommendations, search, and imports going forward. You can unblock any of them anytime from Operations/Blocked Creators."
              : "They'll be removed from your recommendations, search, and imports going forward. You can unblock them anytime from Operations/Blocked Creators."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row items-center justify-end gap-2">
          <Button color="secondary" size="md" isDisabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button color="primary" size="md" isDisabled={busy} onClick={handleBlock}>
            Block Creator
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
